'use client';

import { PropsWithChildren, Children, FC } from 'react';
import { cn } from '@fshr/shared-react';
import { CarouselButtonsComponent } from './carousel-buttons';
import { useOverlayCarousel } from './useOverlayCarousel';

export type CustomPaginationProps = {
	pages: number[];
	currentPage: number;
	goToPage: (page: number) => void;
	totalItems: number;
};

export type CustomArrowsProps = {
	currentPage: number;
	totalPages: number;
	goToPage: (page: number) => void;
};

interface CarouselProps {
	isFullWidth?: boolean;
	className?: string;
	disableButtons?: boolean;
	buttonLabels?: { previous: string; next: string };
	buttonsType?: ('rounded' | 'arrow')[];
	itemClassName?: string;
	carouselItemsContainer?: string;
	gap?: number;
	// Custom components
	CustomPagination?: FC<PropsWithChildren<CustomPaginationProps>>;
	CustomArrows?: FC<PropsWithChildren<CustomArrowsProps>>;
	customItemRenderer?: (item: React.ReactNode, index: number) => React.ReactNode;
}

export function OverlayCarousel({
	isFullWidth = false,
	disableButtons = true,
	buttonLabels = { previous: 'Previous slide', next: 'Next slide' },
	className,
	buttonsType,
	children,
	itemClassName,
	gap,
	carouselItemsContainer,
	CustomPagination,
	CustomArrows,
	customItemRenderer,
}: PropsWithChildren<CarouselProps>) {
	const items = Children.toArray(children);
	const { containerRef, itemRefs, pages, currentPage, goToPage, fullWidthClassNames } =
		useOverlayCarousel({
			items,
			children,
			gap,
		});

	// Render this when there is only one item
	if (items.length <= 1) {
		return (
			<div className="flex gap-150 md:gap-300">
				{items.map((child, i) => (
					<div
						key={i}
						className={cn(
							itemClassName,
							!itemClassName && 'w-[280px]',
							!itemClassName && 'md:w-[400px]'
						)}
					>
						{child}
					</div>
				))}
			</div>
		);
	}

	return (
		<div
			className={cn([
				'relative',
				'overflow-visible',
				{ group: buttonsType?.includes('rounded') || buttonsType?.includes('arrow') },
				{
					flex: buttonsType?.includes('rounded'),
					'flex-col': buttonsType?.includes('rounded'),
					'gap-[15px]': buttonsType?.includes('rounded'),
				},
			])}
		>
			<div
				ref={containerRef}
				className={cn(
					'w-full no-scrollbar overflow-auto',
					{
						[fullWidthClassNames]: isFullWidth,
					},
					className
				)}
			>
				<div
					className={cn(
						['flex', 'w-max', 'items-start', 'relative'],
						gap ? undefined : 'gap-150 md:gap-300',
						'carousel-container',
						carouselItemsContainer
					)}
					style={gap ? { gap: `${gap}px` } : undefined}
				>
					{items.map((child, i) => (
						<div
							key={i}
							ref={el => {
								itemRefs.current[i] = el;
							}}
							className={cn(
								'carousel-item',
								'flex-shrink-0',
								{
									'w-[280px]': !itemClassName,
									'md:w-[400px]': !itemClassName,
								},
								itemClassName
							)}
						>
							{customItemRenderer ? customItemRenderer(child, i) : child}
						</div>
					))}
				</div>
			</div>

			<CarouselButtonsComponent
				disableButtons={disableButtons}
				buttonsType={buttonsType}
				pages={pages}
				currentPage={currentPage}
				goToPage={goToPage}
				buttonLabels={buttonLabels}
				CustomPagination={CustomPagination}
				CustomArrows={CustomArrows}
			/>
		</div>
	);
}
